import React, { useEffect, useMemo } from 'react';
import type { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from 'src/components/app-context';
import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import { ButtonSize } from '@prisjakt/ui/button';
import { IconClose } from '@prisjakt/ui/icon/close';
import { IconButton } from '@prisjakt/ui/button/variants';
import type { TranslateFunc } from 'src/components/translate';
import { formatFilterFn } from 'src/areas/product-collection/utils/filter';
import type { ProductCollectionNonNameSearchFilter } from 'src/areas/product-collection/components/filter/types';
import { productNavigation } from 'src/areas/product/actions/product';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import type {
  BreadcrumbsSlice,
  FilterSlice,
  MetaDataSlice,
  Product,
  ProductCollection,
  ProductsSlice,
} from 'src/types';
import type { AppState } from 'src/reducers';
import { RECENTLY_VISITED_CLICK } from 'src/globals/helpers/navigation-action';
import { RecentProductCollectionMiniCard } from '../mini-card/variants/RecentProductCollectionMiniCard';
import { RecentProductMiniCard } from '../mini-card/variants/RecentProductMiniCard';
import { SkeletonText } from '../skeleton/skeleton-text';
import { SkeletonMedia } from '../skeleton';
import { MiniCard } from '../mini-card';
import { MiniCardHorizontalScroll } from '../horizontal-scroll/variants/mini-card-horizontal-scroll';
import { useGraphQLQuery } from '../data';
import type {
  RecentlyVisitedItem,
  RecentlyVisitedProduct,
  RecentlyVisitedProductCollection,
} from './storage';
import recentlyVisitedQuery from './query.graphql';
import { loadRecentlyVisited, removeRecentlyVisitedItem } from './actions';
import { PopularCategories } from '../popular-categories/popular-categories';

const MAX_RECENTLY_VISITED_ITEMS = 12;

type RecentlyVisitedResponse = {
  recentlyVisitedProducts: Product[];
  productCollections: ProductCollection[];
  popularProductCollections: { name: string; path: string; image: string }[];
};

type ResolvedRecentlyVisitedItem =
  | (RecentlyVisitedProductCollection & {
      productCollection: ProductCollection;
    })
  | (RecentlyVisitedProduct & { product: Product });

const resolveItems = (
  recentlyVisitedItems: RecentlyVisitedItem[],
  data: RecentlyVisitedResponse
) => {
  // For various reasons, some products may be missing from the data array,
  // e.g. if they've been removed since the user visited the product. We must
  // filter out those products and simply show as many recent productcs as
  // possible.
  return recentlyVisitedItems.reduce<ResolvedRecentlyVisitedItem[]>(
    (arr, item) => {
      if (item.type === 'product') {
        const product = data.recentlyVisitedProducts.find(
          p => p.id === item.id
        );
        if (!product) return arr;

        arr.push({ ...item, product });
      } else if (item.type === 'productCollection') {
        const productCollection = data.productCollections.find(
          pc => pc.id === item.id
        );
        if (!productCollection) return arr;

        arr.push({ ...item, productCollection });
      }

      return arr;
    },
    []
  );
};

const createCard = (data: {
  item: ResolvedRecentlyVisitedItem;
  t: TranslateFunc;
  dispatch: Dispatch<any>;
  index: number;
}) => {
  const { item, t, dispatch, index } = data;

  const menu = (
    <IconButton
      onlyIcon
      size={ButtonSize.SMALL}
      onClick={() => dispatch(removeRecentlyVisitedItem(item.id))}
    >
      <IconClose />
    </IconButton>
  );

  if (item.type === 'product') {
    const product = item.product;
    return (
      <RecentProductMiniCard
        key={product.id}
        id={product.id}
        menu={menu}
        title={product.name}
        image={product.media.first}
        rating={
          product.aggregatedRating?.score || product.userReviewSummary?.rating
        }
        ratingCount={
          product.aggregatedRating?.count || product.userReviewSummary?.count
        }
        price={product.priceSummary}
        diffPercentage={product.diffPercentage}
        to={product.pathName}
        onClick={() => {
          dispatch(productNavigation(RECENTLY_VISITED_CLICK, index + 1));
          trackInternalNavigationClick({
            scope: InternalNavigationScope.RECENTLY_VISITED_PRODUCT_CARD,
            nextPage: product.pathName,
            index,
          });
        }}
      />
    );
  }

  const productCollection = item.productCollection;
  const productsSlice = productCollection.slices.find(
    slice => slice.__typename === 'ProductsSlice'
  ) as ProductsSlice | undefined;
  const breadcrumbsSlice = productCollection.slices.find(
    slice => slice.__typename === 'BreadcrumbsSlice'
  ) as BreadcrumbsSlice | undefined;
  const filterSlice = productCollection.slices.find(
    slice => slice.__typename === 'FilterSlice'
  ) as FilterSlice | undefined;
  const metaDataSlice = productCollection.slices.find(
    slice => slice.__typename === 'MetaDataSlice'
  ) as MetaDataSlice | undefined;
  const image =
    productsSlice?.products[0]?.media?.first ?? metaDataSlice?.image;
  const name =
    breadcrumbsSlice?.items.slice(-1)[0]?.name ?? productCollection.name ?? '';
  const filters = (filterSlice?.filters ??
    []) as unknown as ProductCollectionNonNameSearchFilter[];

  return (
    <RecentProductCollectionMiniCard
      key={item.id}
      id={item.id}
      title={name}
      image={image}
      menu={menu}
      productCount={filterSlice?.productsCount}
      filters={filters.map(filter => {
        const { title, value } = formatFilterFn({ filter, t });

        if (filter.__typename === 'ProductCollectionBooleanFilter') {
          return `${title}: ${value}`;
        }

        return value;
      })}
      to={item.url}
      onClick={() => {
        trackInternalNavigationClick({
          scope:
            InternalNavigationScope.RECENTLY_VISITED_PRODUCT_COLLECTION_CARD,
          nextPage: item.url,
          index,
        });
      }}
    />
  );
};

const createRange = (range: number) => [...Array(range).keys()];

type RecentlyVisitedProps = {
  negativeMargin?: boolean;
  offset?: number;
};

const RecentlyVisited: React.FC<RecentlyVisitedProps> = props => {
  const { t } = useAppContext();
  const dispatch = useDispatch();

  const allRecentlyVisitedItems = useSelector((app: AppState) =>
    (app.recentlyVisited.items ?? []).slice(props.offset ?? 0)
  );

  const recentlyVisitedLoaded = useSelector(
    (app: AppState) => app.recentlyVisited.loaded
  );

  useEffect(() => {
    dispatch(loadRecentlyVisited());
  }, [dispatch]);

  const variables = useMemo(() => {
    const recentlyVisitedItems = allRecentlyVisitedItems.slice(
      0,
      MAX_RECENTLY_VISITED_ITEMS
    );
    const recentlyVisitedProductIds = recentlyVisitedItems
      .filter((item): item is RecentlyVisitedProduct => item.type === 'product')
      .map(p => p.id)
      .filter(Boolean);

    const productCollections = recentlyVisitedItems
      .filter(
        (item): item is RecentlyVisitedProductCollection =>
          item.type === 'productCollection'
      )
      .map(pc => ({
        id: pc.id,
        slug: pc.slug,
        url: pc.url,
      }));

    return {
      recentlyVisitedProductIds,
      productCollections,
      productCollectionQuery: {
        limit: 1,
      },
    };
  }, [allRecentlyVisitedItems]);

  const { loading, data, refresh } = useGraphQLQuery<RecentlyVisitedResponse>({
    query: recentlyVisitedQuery,
    variables,
  });

  const resolvedItems = useMemo(() => {
    if (
      !data ||
      (!data.productCollections?.length &&
        !data.recentlyVisitedProducts?.length)
    ) {
      return null;
    }

    return resolveItems(
      allRecentlyVisitedItems.slice(0, MAX_RECENTLY_VISITED_ITEMS),
      data
    );
  }, [allRecentlyVisitedItems, data]);

  useEffect(() => {
    if (recentlyVisitedLoaded) {
      refresh();
    }
  }, [recentlyVisitedLoaded]);

  if (FEATURE_TOGGLE >= 1) return null;

  if (
    recentlyVisitedLoaded &&
    !allRecentlyVisitedItems.length &&
    data?.popularProductCollections
  ) {
    return (
      <PopularCategories
        negativeMargin
        categories={data?.popularProductCollections}
      />
    );
  }

  return (
    <Panel>
      {loading || !resolvedItems ? (
        <>
          <SkeletonText width="35%" />
          <MiniCardHorizontalScroll
            negativeMargin={props.negativeMargin}
            buttonSize={ButtonSize.SMALL}
          >
            {createRange(3).map(i => (
              <MiniCard
                key={i}
                image={<SkeletonMedia height={72} width={72} />}
              >
                <SkeletonText marginBottom="spacing100" />
                <SkeletonText marginBottom="spacing100" />
                <SkeletonText width="50%" />
              </MiniCard>
            ))}
          </MiniCardHorizontalScroll>
        </>
      ) : (
        <>
          <PanelHeader>
            <PanelTitle>{t('landing', 'recently-visited.title')}</PanelTitle>
          </PanelHeader>
          <MiniCardHorizontalScroll
            negativeMargin={props.negativeMargin}
            buttonSize={ButtonSize.SMALL}
          >
            {resolvedItems.map((item, index) =>
              createCard({ item, t, dispatch, index })
            )}
          </MiniCardHorizontalScroll>
        </>
      )}
    </Panel>
  );
};

export default RecentlyVisited;
